import { LANE_COUNT, TRACK_LENGTH_M, type Entrant, type Key } from '@sprinters/shared';
import { formatTime } from '../format';
import type { RaceFrame, RunnerView } from './SnapshotBuffer';
import { drawRunner } from './runnerSprite';

/** Données nécessaires pour dessiner une image. */
export interface RaceView {
  lanes: readonly Entrant[];
  /** Couloir du joueur local (0-7), -1 s'il regarde en spectateur. */
  myLane: number;
  frame: RaceFrame | null;
  /** Texte de décompte (« 3 », « PARTEZ ! »...) ou `null`. */
  countdownText: string | null;
  pressed: Readonly<Record<Key, boolean>>;
}

const WIDTH = 1200;
const HEIGHT = 640;
const GUTTER = 190;
const TOP = 44;
const LANE_HEIGHT = 68;
const START_X = GUTTER + 26;
const FINISH_X = WIDTH - 46;
const FOOTER_Y = TOP + LANE_HEIGHT * LANE_COUNT;

const LANE_COLORS = [
  '#ff5d6c',
  '#ffa94d',
  '#ffe066',
  '#51cf66',
  '#4dd8e6',
  '#5c7cfa',
  '#b197fc',
  '#f783ac',
] as const;

const FONT = '"Segoe UI", system-ui, -apple-system, Roboto, sans-serif';

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function rankOf(runners: readonly RunnerView[], lane: number): number {
  const me = runners[lane];
  if (!me) return 1;
  let rank = 1;
  runners.forEach((other, index) => {
    if (index === lane) return;
    const ahead =
      me.finishTimeMs !== null
        ? other.finishTimeMs !== null && other.finishTimeMs < me.finishTimeMs
        : other.finishTimeMs !== null || other.x > me.x;
    if (ahead) rank++;
  });
  return rank;
}

/** Dessine la piste, les coureurs et l'interface de course sur un canvas 2D. */
export class RaceRenderer {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly observer: ResizeObserver;
  private frameRequest = 0;
  private getView: (() => RaceView | null) | null = null;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D indisponible');
    this.ctx = ctx;
    this.observer = new ResizeObserver(() => this.resize());
    this.observer.observe(canvas);
    this.resize();
  }

  start(getView: () => RaceView | null): void {
    this.getView = getView;
    const loop = (now: number): void => {
      this.frameRequest = requestAnimationFrame(loop);
      this.draw(now);
    };
    this.frameRequest = requestAnimationFrame(loop);
  }

  destroy(): void {
    cancelAnimationFrame(this.frameRequest);
    this.observer.disconnect();
  }

  private resize(): void {
    const ratio = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.round(this.canvas.clientWidth * ratio));
    const height = Math.round((width * HEIGHT) / WIDTH);
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
  }

  private draw(now: number): void {
    const { ctx, canvas } = this;
    ctx.setTransform(canvas.width / WIDTH, 0, 0, canvas.width / WIDTH, 0, 0);
    ctx.clearRect(0, 0, WIDTH, HEIGHT);
    this.drawBackground();

    const view = this.getView?.() ?? null;
    if (!view) return;

    this.drawTrack(view);
    view.lanes.forEach((entrant, lane) => this.drawLane(view, entrant, lane, now));
    this.drawFooter(view);
    if (view.countdownText) this.drawCountdown(view.countdownText);
    this.drawFallBanner(view, now);
  }

  private drawBackground(): void {
    const { ctx } = this;
    const gradient = ctx.createLinearGradient(0, 0, 0, HEIGHT);
    gradient.addColorStop(0, '#141a2b');
    gradient.addColorStop(1, '#0b0f1a');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, WIDTH, HEIGHT);
  }

  private laneTop(lane: number): number {
    return TOP + lane * LANE_HEIGHT;
  }

  private drawTrack(view: RaceView): void {
    const { ctx } = this;

    for (let lane = 0; lane < LANE_COUNT; lane++) {
      const y = this.laneTop(lane);
      ctx.fillStyle = lane % 2 === 0 ? '#9a4a35' : '#8c4230';
      ctx.fillRect(GUTTER, y, WIDTH - GUTTER, LANE_HEIGHT);
      ctx.fillStyle = lane % 2 === 0 ? '#1d2438' : '#182033';
      ctx.fillRect(0, y, GUTTER, LANE_HEIGHT);
    }

    if (view.myLane >= 0) {
      const y = this.laneTop(view.myLane);
      ctx.fillStyle = 'rgba(255, 224, 102, 0.16)';
      ctx.fillRect(0, y, WIDTH, LANE_HEIGHT);
      ctx.strokeStyle = 'rgba(255, 224, 102, 0.85)';
      ctx.lineWidth = 2;
      ctx.strokeRect(1, y + 1, WIDTH - 2, LANE_HEIGHT - 2);
    }

    // Lignes de couloirs.
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
    ctx.lineWidth = 2;
    for (let lane = 1; lane < LANE_COUNT; lane++) {
      const y = this.laneTop(lane);
      ctx.beginPath();
      ctx.moveTo(GUTTER, y);
      ctx.lineTo(WIDTH, y);
      ctx.stroke();
    }

    // Repères tous les 10 m.
    ctx.font = `600 12px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.14)';
    ctx.lineWidth = 1;
    for (let meters = 10; meters < TRACK_LENGTH_M; meters += 10) {
      const x = this.xForDistance(meters);
      ctx.beginPath();
      ctx.moveTo(x, TOP);
      ctx.lineTo(x, FOOTER_Y);
      ctx.stroke();
      ctx.fillText(`${meters} m`, x, TOP - 10);
    }

    // Ligne de départ.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(START_X - 3, TOP, 4, LANE_COUNT * LANE_HEIGHT);

    // Ligne d'arrivée en damier.
    const square = 9;
    for (let row = 0; row < (LANE_COUNT * LANE_HEIGHT) / square; row++) {
      for (let col = 0; col < 2; col++) {
        ctx.fillStyle = (row + col) % 2 === 0 ? '#ffffff' : '#111111';
        ctx.fillRect(FINISH_X + col * square, TOP + row * square, square, square);
      }
    }
    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.fillText('ARRIVÉE', FINISH_X + square, TOP - 10);
  }

  private xForDistance(meters: number): number {
    return START_X + (meters / TRACK_LENGTH_M) * (FINISH_X - START_X);
  }

  private drawLane(view: RaceView, entrant: Entrant, lane: number, now: number): void {
    const { ctx } = this;
    const top = this.laneTop(lane);
    const isMe = lane === view.myLane;
    const runner = view.frame?.runners[lane];
    const color = LANE_COLORS[lane] ?? '#ffffff';

    // Étiquette de couloir.
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(24, top + LANE_HEIGHT / 2, 13, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#0b0f1a';
    ctx.font = `800 15px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(lane + 1), 24, top + LANE_HEIGHT / 2 + 1);

    ctx.textAlign = 'left';
    ctx.font = `${isMe ? 800 : 600} 16px ${FONT}`;
    ctx.fillStyle = isMe ? '#ffe066' : entrant.kind === 'bot' ? '#98a2bd' : '#ffffff';
    const label = truncate(entrant.name, 15);
    ctx.fillText(
      label,
      46,
      top +
        LANE_HEIGHT / 2 -
        (runner && (runner.falls > 0 || runner.finishTimeMs !== null) ? 8 : 0),
    );

    if (runner) {
      ctx.font = `600 12px ${FONT}`;
      if (runner.finishTimeMs !== null) {
        ctx.fillStyle = '#8ce99a';
        ctx.fillText(formatTime(runner.finishTimeMs), 46, top + LANE_HEIGHT / 2 + 12);
      } else if (runner.falls > 0) {
        ctx.fillStyle = '#ff8787';
        ctx.fillText(
          `${runner.falls} chute${runner.falls > 1 ? 's' : ''}`,
          46,
          top + LANE_HEIGHT / 2 + 12,
        );
      }
    }

    // Coureur.
    drawRunner(ctx, {
      x: this.xForDistance(runner?.x ?? 0),
      groundY: top + LANE_HEIGHT * 0.82,
      color,
      distanceM: runner?.x ?? 0,
      speed: runner?.v ?? 0,
      phase: runner?.phase ?? 'idle',
      clockMs: now,
    });

    if (isMe && runner?.phase !== 'finished') {
      const x = this.xForDistance(runner?.x ?? 0);
      ctx.fillStyle = '#ffe066';
      ctx.beginPath();
      ctx.moveTo(x - 6, top + 4);
      ctx.lineTo(x + 6, top + 4);
      ctx.lineTo(x, top + 12);
      ctx.closePath();
      ctx.fill();
    }

    // Médaille de classement une fois la ligne franchie.
    if (runner?.finishTimeMs !== null && runner && view.frame) {
      const rank = rankOf(view.frame.runners, lane);
      const badgeX = FINISH_X + 27;
      ctx.fillStyle =
        rank === 1 ? '#ffd43b' : rank === 2 ? '#ced4da' : rank === 3 ? '#e8905a' : '#495057';
      ctx.beginPath();
      ctx.arc(badgeX, top + LANE_HEIGHT / 2, 12, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#10131f';
      ctx.font = `800 14px ${FONT}`;
      ctx.textAlign = 'center';
      ctx.fillText(String(rank), badgeX, top + LANE_HEIGHT / 2 + 1);
    }
  }

  private drawFooter(view: RaceView): void {
    const { ctx } = this;
    const centerY = FOOTER_Y + (HEIGHT - FOOTER_Y) / 2;
    const me = view.myLane >= 0 ? view.frame?.runners[view.myLane] : undefined;

    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';

    if (view.myLane < 0) {
      ctx.fillStyle = '#98a2bd';
      ctx.font = `600 18px ${FONT}`;
      ctx.fillText('Vous regardez la course en spectateur', 24, centerY);
      return;
    }

    const raceTime = me?.finishTimeMs ?? Math.max(0, view.frame?.t ?? 0);
    ctx.fillStyle = '#ffffff';
    ctx.font = `800 30px ${FONT}`;
    ctx.fillText(formatTime(raceTime), 24, centerY);

    if (view.frame && me) {
      ctx.font = `600 16px ${FONT}`;
      ctx.fillStyle = '#ffe066';
      ctx.fillText(
        `${rankOf(view.frame.runners, view.myLane)}ᵉ / ${view.lanes.length}`,
        190,
        centerY,
      );
    }

    // Flèches, éclairées quand la touche est enfoncée.
    const pads: readonly { key: Key; x: number; glyph: string }[] = [
      { key: 'left', x: WIDTH / 2 - 92, glyph: '←' },
      { key: 'right', x: WIDTH / 2 + 22, glyph: '→' },
    ];
    for (const pad of pads) {
      const active = view.pressed[pad.key];
      ctx.fillStyle = active ? '#ffe066' : 'rgba(255, 255, 255, 0.1)';
      ctx.beginPath();
      ctx.roundRect(pad.x, centerY - 19, 70, 38, 8);
      ctx.fill();
      ctx.fillStyle = active ? '#10131f' : '#ced4da';
      ctx.font = `800 24px ${FONT}`;
      ctx.textAlign = 'center';
      ctx.fillText(pad.glyph, pad.x + 35, centerY + 1);
    }

    // Jauge de vitesse.
    const speed = me?.v ?? 0;
    const gaugeX = WIDTH - 280;
    ctx.textAlign = 'right';
    ctx.fillStyle = '#98a2bd';
    ctx.font = `600 14px ${FONT}`;
    ctx.fillText(`${speed.toFixed(1).replace('.', ',')} m/s`, WIDTH - 24, centerY);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.beginPath();
    ctx.roundRect(gaugeX, centerY - 6, 150, 12, 6);
    ctx.fill();
    ctx.fillStyle = speed > 9 ? '#51cf66' : speed > 6 ? '#ffe066' : '#ffa94d';
    ctx.beginPath();
    ctx.roundRect(gaugeX, centerY - 6, Math.max(6, Math.min(1, speed / 11) * 150), 12, 6);
    ctx.fill();
  }

  private drawCountdown(text: string): void {
    const { ctx } = this;
    ctx.save();
    ctx.fillStyle = 'rgba(6, 8, 14, 0.55)';
    ctx.fillRect(GUTTER, TOP, WIDTH - GUTTER, LANE_COUNT * LANE_HEIGHT);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const isGo = text === 'PARTEZ !';
    ctx.font = `900 ${isGo ? 96 : text.length > 2 ? 64 : 150}px ${FONT}`;
    ctx.lineWidth = 8;
    ctx.strokeStyle = 'rgba(8, 10, 18, 0.8)';
    ctx.fillStyle = isGo ? '#51cf66' : '#ffe066';
    const x = (GUTTER + WIDTH) / 2;
    const y = TOP + (LANE_COUNT * LANE_HEIGHT) / 2;
    ctx.strokeText(text, x, y);
    ctx.fillText(text, x, y);
    ctx.restore();
  }

  private drawFallBanner(view: RaceView, now: number): void {
    const me = view.myLane >= 0 ? view.frame?.runners[view.myLane] : undefined;
    if (me?.phase !== 'fallen') return;
    const { ctx } = this;
    if (Math.floor(now / 200) % 2 === 0) return;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `900 44px ${FONT}`;
    ctx.lineWidth = 7;
    ctx.strokeStyle = 'rgba(8, 10, 18, 0.85)';
    ctx.fillStyle = '#ff6b6b';
    const x = (GUTTER + WIDTH) / 2;
    const y = this.laneTop(view.myLane) + LANE_HEIGHT / 2;
    ctx.strokeText('CHUTE !', x, y);
    ctx.fillText('CHUTE !', x, y);
    ctx.restore();
  }
}
